import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { ShortTxnResponse } from 'app/modules/transactions/types';

import { getTagDisplayName } from 'app/modules/tags/utils';

import {
  IconBriefcase,
  IconFlag,
  IconFlagDown,
  IconTag,
} from '@u21/tabler-icons';
import {
  U21TableBodyCellProps,
  U21TableColumn,
  U21Tooltip,
  U21Spacer,
  U21ShowMoreList,
  U21NoValue,
} from 'app/shared/u21-ui/components';
import { FullAttachmentResponse } from 'app/modules/attachments/responses';
import { TxnDocumentChipList } from 'app/modules/transactions/components/TxnDocumentChipList';
import styled from 'styled-components';
import { selectDataSettingsById } from 'app/modules/dataSettings/selectors';
import { DataSettingDataDisplay } from 'app/modules/dataSettings/shared/DataSettingDataDisplay';
import { getDataSettingIdsByFlow } from 'app/modules/transactions/utils';
import { FormattedData } from 'app/modules/dataSettings/responses';
import { TransactionRiskScore } from 'app/modules/transactionScore/components/TransactionRiskScore';
import {
  TXN_ANALYSIS_NO_CASE_MAP,
  TxnAnalysisType,
} from 'app/modules/txnAnalysis/constants';
import { DeviceIntelligenceChipPopOver } from 'app/modules/deviceIntelligence/components/DeviceIntelligenceChipPopOver';

export type TransactionTableColumn = U21TableColumn<ShortTxnResponse, any>;

const RECEIVER_INSTRUMENT_TYPE: TransactionTableColumn = {
  id: 'receiver_instrument.instrument_type',
  accessor: (row) => row['receiver_instrument.instrument_type'],
};

export const FlowColumnChip = ({
  formattedData,
  id,
}: {
  formattedData: FormattedData;
  id: string;
}) => {
  const dataSetting = useSelector(selectDataSettingsById)[id];
  return (
    <DataSettingDataDisplay
      key={id}
      formattedData={formattedData}
      dataSetting={dataSetting}
    />
  );
};

export const OUTBOUND_COLUMN: TransactionTableColumn = {
  Header: 'Sending',
  id: 'outbound_flow',
  Cell: (props: U21TableBodyCellProps<ShortTxnResponse, string>) => {
    const { row } = props;
    const rowData = getDataSettingIdsByFlow(row.formatted_data, 'OUTBOUND');
    return (
      <StyledFlowMoreList
        displayFunc={(id) => {
          return (
            <FlowColumnChip
              key={id}
              formattedData={row.formatted_data}
              id={id}
            />
          );
        }}
        onLessClick={(e) => e.stopPropagation()}
        onMoreClick={(e) => e.stopPropagation()}
        value={rowData}
      />
    );
  },
};

export const INBOUND_COLUMN: TransactionTableColumn = {
  Header: 'Receiving',
  id: 'inbound_flow',
  Cell: (props: U21TableBodyCellProps<ShortTxnResponse, string>) => {
    const { row } = props;
    const rowData = getDataSettingIdsByFlow(row.formatted_data, 'INBOUND');
    return (
      <StyledFlowMoreList
        displayFunc={(id) => {
          return (
            <FlowColumnChip
              key={id}
              formattedData={row.formatted_data}
              id={id}
            />
          );
        }}
        onLessClick={(e) => e.stopPropagation()}
        onMoreClick={(e) => e.stopPropagation()}
        value={rowData}
      />
    );
  },
};

export const OTHER_FLOW_COLUMN: TransactionTableColumn = {
  Header: 'Other directionality',
  id: 'other_flow',
  Cell: (props: U21TableBodyCellProps<ShortTxnResponse, string>) => {
    const { row } = props;
    const rowData = getDataSettingIdsByFlow(row.formatted_data, 'OTHER');

    return (
      <StyledFlowMoreList
        displayFunc={(id) => {
          return (
            <FlowColumnChip
              key={id}
              formattedData={row.formatted_data}
              id={id}
            />
          );
        }}
        onLessClick={(e) => e.stopPropagation()}
        onMoreClick={(e) => e.stopPropagation()}
        value={rowData}
      />
    );
  },
};

const SENDER_INSTRUMENT_TYPE: TransactionTableColumn = {
  id: 'sender_instrument.instrument_type',
  accessor: (row) => row['sender_instrument.instrument_type'],
};

// API Field Naming: Backend /txn-events/list returns 'sender_instrument.subtype' (short name),
// but other contexts use 'sender_instrument_subtype'. The accessor must match the API response.
// Column ID uses full name 'sender_instrument.instrument_subtype' for table config consistency.
const SENDER_INSTRUMENT_SUBTYPE: TransactionTableColumn = {
  id: 'sender_instrument.instrument_subtype',
  accessor: (row) => row['sender_instrument.subtype'],
};

const RECEIVER_INSTRUMENT_SUBTYPE: TransactionTableColumn = {
  id: 'receiver_instrument.instrument_subtype',
  accessor: (row) => row['receiver_instrument.subtype'],
};

const TAGS_COLUMN: TransactionTableColumn = {
  id: 'tags',
  accessor: (row) => row.tags.map((tag) => getTagDisplayName(tag)),
  cellProps: {
    getShowLessText: () => 'Show fewer tags',
    getShowMoreText: (length: number) => `Show all ${length} tags`,
  },
  headerIcon: <IconTag />,
  type: 'list',
  disableSortBy: true,
};

export const getAssociatedLinks = (
  type: TxnAnalysisType,
): TransactionTableColumn => ({
  id: 'associated_links',
  Header: 'Associated links',
  accessor: (row) => (
    <U21Spacer horizontal>
      {row.has_alerts && (
        <U21Tooltip tooltip="This transaction has been flagged in an alert">
          <IconFlag />
        </U21Tooltip>
      )}
      {row.in_article && (
        <U21Tooltip
          tooltip={`This transaction is associated with the current ${TXN_ANALYSIS_NO_CASE_MAP[type]}`}
        >
          {type === TxnAnalysisType.CASE ? <IconBriefcase /> : <IconFlagDown />}
        </U21Tooltip>
      )}
    </U21Spacer>
  ),
  disableSortBy: true,
});

const DOCUMENT_COLUMN: TransactionTableColumn = {
  id: 'documents',
  disableSortBy: true,
  Cell: (
    props: U21TableBodyCellProps<ShortTxnResponse, FullAttachmentResponse[]>,
  ) => {
    const { row } = props;
    if (isEmpty(row.documents)) {
      return <U21NoValue />;
    }
    return <TxnDocumentChipList txnEvent={row} />;
  },
};

const U21_RISK_SCORE_COLUMN: TransactionTableColumn = {
  id: 'u21_risk_score',
  Cell: (
    props: U21TableBodyCellProps<ShortTxnResponse, FullAttachmentResponse[]>,
  ) => {
    const { row } = props;
    if (row.u21_risk_score === null) {
      return <U21NoValue />;
    }
    return (
      <TransactionRiskScore
        eventID={row.external_id}
        score={row.u21_risk_score}
      />
    );
  },
};

const DEVICE_SIGNALS_COLUMN: TransactionTableColumn = {
  id: 'device_intelligence',
  Header: 'Device Signals',
  disableSortBy: true,
  Cell: ({ row }: U21TableBodyCellProps<ShortTxnResponse>) => (
    <DeviceIntelligenceChipPopOver event="txn" row={row} />
  ),
};

export const TRANSACTION_COLUMN_CONFIG = {
  [DEVICE_SIGNALS_COLUMN.id]: DEVICE_SIGNALS_COLUMN,
  [DOCUMENT_COLUMN.id]: DOCUMENT_COLUMN,
  [INBOUND_COLUMN.id]: INBOUND_COLUMN,
  [OTHER_FLOW_COLUMN.id]: OTHER_FLOW_COLUMN,
  [OUTBOUND_COLUMN.id]: OUTBOUND_COLUMN,
  [RECEIVER_INSTRUMENT_SUBTYPE.id]: RECEIVER_INSTRUMENT_SUBTYPE,
  [RECEIVER_INSTRUMENT_TYPE.id]: RECEIVER_INSTRUMENT_TYPE,
  [SENDER_INSTRUMENT_SUBTYPE.id]: SENDER_INSTRUMENT_SUBTYPE,
  [SENDER_INSTRUMENT_TYPE.id]: SENDER_INSTRUMENT_TYPE,
  [TAGS_COLUMN.id]: TAGS_COLUMN,
  [U21_RISK_SCORE_COLUMN.id]: U21_RISK_SCORE_COLUMN,
};

const StyledFlowMoreList = styled(U21ShowMoreList<string>)`
  width: 100%;
`;
